import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtInt, formatApiError } from "@/lib/api";
import { ArrowLeft, AlertTriangle, CheckCircle2, ClipboardCheck, X } from "lucide-react";

const TYPE_STYLES = {
  CREATED: { label: "Created", color: "#475569", bg: "#F1F5F9" },
  RESTOCK: { label: "Restock", color: "#15803D", bg: "#F0FDF4" },
  SALE: { label: "Sale", color: "#B45309", bg: "#FFFBEB" },
  DEFECT: { label: "Defect Sale", color: "#B91C1C", bg: "#FEF2F2" },
  INVOICE_CANCELLED: { label: "Invoice Cancelled (Restored)", color: "#0369A1", bg: "#F0F9FF" },
  RETURN: { label: "Customer Return", color: "#0369A1", bg: "#F0F9FF" },
  ADJUSTMENT: { label: "Manual Adjustment", color: "#7C3AED", bg: "#F5F3FF" },
  DELETED: { label: "Deleted", color: "#7F1D1D", bg: "#FEE2E2" },
};

export default function ProductActivityLog() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [selectedColors, setSelectedColors] = useState([]); // empty = all colors
  const [adjusting, setAdjusting] = useState(false);

  const reload = () => api.get(`/products/${id}/activity-log`).then(({ data }) => setData(data));

  useEffect(() => {
    reload().catch(() => setErr("Could not load activity log."));
  }, [id]);

  const toggleColor = (color) => {
    setSelectedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  };
  const clearColors = () => setSelectedColors([]);

  const filteredEvents = useMemo(() => {
    if (!data) return [];
    if (selectedColors.length === 0) return data.events;
    return data.events.filter((e) => e.color === null || selectedColors.includes(e.color));
  }, [data, selectedColors]);

  // Aggregate stats for the currently selected color(s), or overall if none selected
  const displayStats = useMemo(() => {
    if (!data) return null;
    if (selectedColors.length === 0) {
      return {
        current: data.current_total_stock,
        expected: data.expected_total_stock,
        mismatch: data.mismatch,
        totals: data.totals,
      };
    }
    const totals = { initial: 0, restocked: 0, sold: 0, cancelled_restored: 0, returned: 0, adjusted: 0 };
    let current = 0, expected = 0, hasExpected = data.has_full_history;
    selectedColors.forEach((color) => {
      const s = data.color_summary[color];
      if (!s) return;
      current += s.current;
      if (hasExpected && s.expected != null) expected += s.expected;
      Object.keys(totals).forEach((k) => { totals[k] += s[k]; });
    });
    return {
      current,
      expected: hasExpected ? expected : null,
      mismatch: hasExpected ? current - expected : null,
      totals,
    };
  }, [data, selectedColors]);

  if (err) return <div className="error-msg">{err}</div>;
  if (!data) return <div style={{ padding: 40, color: "var(--text-muted)" }}>Loading...</div>;

  const { has_full_history } = data;
  const { current, expected, mismatch, totals } = displayStats;

  return (
    <div data-testid="product-activity-log-page">
      <div className="page-header">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => nav("/products")} style={{ marginBottom: 8, paddingLeft: 0 }}>
            <ArrowLeft size={14}/> Back to Products
          </button>
          <h1 className="page-title">{data.product.style_id} — Activity Log</h1>
          <p className="page-sub">{data.product.name}</p>
        </div>
        <button className="btn btn-secondary" data-testid="record-count-btn" onClick={() => setAdjusting(true)}>
          <ClipboardCheck size={16}/> Record Physical Count
        </button>
      </div>

      {/* Current stock, size-wise, per color */}
      <div className="card" style={{ marginBottom: 16 }} data-testid="current-stock-breakdown">
        <div style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
          Current Stock — Size Breakdown
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Color</th>
              {data.colors_detail[0] && Object.keys(data.colors_detail[0].sizes).map((s) => <th key={s} className="num">{s}</th>)}
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.colors_detail.map((c) => {
              const total = Object.values(c.sizes).reduce((a, q) => a + Number(q || 0), 0);
              return (
                <tr key={c.color}>
                  <td><strong>{c.color}</strong></td>
                  {Object.keys(c.sizes).map((s) => <td key={s} className="num">{c.sizes[s]}</td>)}
                  <td className="num"><strong>{total}</strong></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.colors.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }} data-testid="color-filter">
          <div style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
            Filter by Color
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {data.colors.map((color) => {
              const active = selectedColors.includes(color);
              return (
                <button
                  key={color}
                  data-testid={`color-filter-${color}`}
                  onClick={() => toggleColor(color)}
                  className="badge"
                  style={{
                    cursor: "pointer", border: active ? "1px solid var(--accent)" : "1px solid var(--border)",
                    background: active ? "var(--accent)" : "white", color: active ? "white" : "var(--text)",
                    padding: "6px 12px", fontWeight: 600,
                  }}
                >
                  {color}
                </button>
              );
            })}
            {selectedColors.length > 0 && (
              <button className="btn btn-ghost btn-sm" data-testid="clear-color-filter" onClick={clearColors}>Clear (show all)</button>
            )}
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }} data-testid="reconciliation-panel">
        {selectedColors.length > 0 && (
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
            Showing stats for: <strong>{selectedColors.join(", ")}</strong>
          </div>
        )}
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.6 }}>Current Stock</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtInt(current)}</div>
          </div>
          {has_full_history && (
            <div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.6 }}>Expected Stock</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtInt(expected)}</div>
            </div>
          )}
          {has_full_history && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 6, background: mismatch === 0 ? "#F0FDF4" : "#FEF2F2", border: `1px solid ${mismatch === 0 ? "#BBF7D0" : "#FECACA"}` }}>
              {mismatch === 0 ? (
                <>
                  <CheckCircle2 size={18} color="#15803D"/>
                  <span style={{ color: "#15803D", fontWeight: 600 }}>Fully reconciled — no discrepancy</span>
                </>
              ) : (
                <>
                  <AlertTriangle size={18} color="#B91C1C"/>
                  <span style={{ color: "#B91C1C", fontWeight: 600 }}>
                    Mismatch: {mismatch > 0 ? "+" : ""}{mismatch} piece{Math.abs(mismatch) !== 1 ? "s" : ""} unaccounted for
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        {!has_full_history && (
          <p style={{ marginTop: 12, fontSize: 13, color: "var(--text-muted)" }}>
            This product was added before we started tracking stock history, so we can't check for mismatches on it yet.
          </p>
        )}
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)", fontSize: 13 }}>
          {totals.initial != null && <span>Initial: <strong>{fmtInt(totals.initial)}</strong></span>}
          <span>Restocked: <strong style={{ color: "#15803D" }}>+{fmtInt(totals.restocked)}</strong></span>
          <span>Sold: <strong style={{ color: "#B45309" }}>-{fmtInt(totals.sold)}</strong></span>
          <span>Cancelled (Restored): <strong style={{ color: "#0369A1" }}>+{fmtInt(totals.cancelled_restored)}</strong></span>
          <span>Returned: <strong style={{ color: "#0369A1" }}>+{fmtInt(totals.returned)}</strong></span>
          {totals.adjusted !== 0 && <span>Adjusted: <strong style={{ color: "#7C3AED" }}>{totals.adjusted > 0 ? "+" : ""}{fmtInt(totals.adjusted)}</strong></span>}
        </div>
      </div>

      <div className="card-flush">
        {filteredEvents.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>No activity for this selection.</div>
        )}
        {filteredEvents.map((e, i) => {
          const style = TYPE_STYLES[e.type] || { label: e.type, color: "#475569", bg: "#F8FAFC" };
          return (
            <div key={i} style={{ padding: "14px 20px", borderBottom: i < filteredEvents.length - 1 ? "1px solid var(--border)" : "none", display: "flex", gap: 14, alignItems: "flex-start" }} data-testid={`activity-event-${i}`}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: style.bg, color: style.color, whiteSpace: "nowrap", marginTop: 2 }}>
                {style.label}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                    {e.color && <strong style={{ color: "var(--text)" }}>{e.color}</strong>}{e.color && " · "}
                    {e.date ? new Date(e.date).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—"}
                  </span>
                  {e.qty_change != null && (
                    <strong style={{ color: e.qty_change > 0 ? "#15803D" : e.qty_change < 0 ? "#B91C1C" : "var(--text-muted)" }}>
                      {e.qty_change > 0 ? "+" : ""}{e.qty_change}
                    </strong>
                  )}
                </div>
                <div style={{ fontSize: 13, marginTop: 4 }}>{e.detail}</div>
                {e.invoice_number && (
                  <Link to={`/invoices/${e.invoice_id}`} style={{ fontSize: 12.5, color: "var(--accent)", textDecoration: "none" }}>
                    View {e.invoice_number} {e.customer ? `— ${e.customer}` : ""}
                  </Link>
                )}
                {e.return_number && (
                  <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                    {e.return_number} {e.customer ? `— ${e.customer}` : ""}
                  </span>
                )}
                {e.note && <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>Note: {e.note}</div>}
                {e.reason && <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>Reason: {e.reason}{e.adjusted_by ? ` — by ${e.adjusted_by}` : ""}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {adjusting && (
        <AdjustmentModal
          product={data.product}
          productId={id}
          colors={data.colors}
          colorsDetail={data.colors_detail}
          onClose={() => setAdjusting(false)}
          onSaved={async () => { setAdjusting(false); await reload(); }}
        />
      )}
    </div>
  );
}

function AdjustmentModal({ product, productId, colors, colorsDetail, onClose, onSaved }) {
  const [color, setColor] = useState(colors[0] || "");
  const currentColorData = colorsDetail.find((c) => c.color === color);
  const sizeKeys = currentColorData ? Object.keys(currentColorData.sizes) : [];

  const [counts, setCounts] = useState(() =>
    Object.fromEntries(sizeKeys.map((s) => [s, currentColorData?.sizes[s] ?? 0]))
  );
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onColorChange = (newColor) => {
    setColor(newColor);
    const cd = colorsDetail.find((c) => c.color === newColor);
    const keys = cd ? Object.keys(cd.sizes) : [];
    setCounts(Object.fromEntries(keys.map((s) => [s, cd?.sizes[s] ?? 0])));
  };

  const updateCount = (s, v) => setCounts((prev) => ({ ...prev, [s]: Math.max(0, Number(v) || 0) }));

  const changedSizes = sizeKeys.filter((s) => Number(counts[s]) !== Number(currentColorData?.sizes[s] ?? 0));

  const save = async () => {
    setErr("");
    if (changedSizes.length === 0) {
      setErr("Change at least one size's count before saving.");
      return;
    }
    if (!reason.trim()) {
      setErr("A reason is required — e.g. damaged, miscounted, found extra.");
      return;
    }
    setBusy(true);
    try {
      const physical_counts = Object.fromEntries(changedSizes.map((s) => [s, Number(counts[s])]));
      await api.post("/stock-adjustments", {
        product_id: productId,
        color,
        physical_counts,
        reason: reason.trim(),
      });
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="adjustment-modal" style={{ maxWidth: 560 }}>
        <div className="modal-h">
          <h2>Record Physical Count</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-b">
          {err && <div className="error-msg" style={{ marginBottom: 12 }}>{err}</div>}
          <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 14 }}>
            Only change the sizes that actually differ from what you counted. Sizes left unchanged won't be touched or logged.
          </p>
          <div className="field" style={{ marginBottom: 14 }}>
            <label>Color *</label>
            <select className="select" data-testid="adj-color" value={color} onChange={(e) => onColorChange(e.target.value)}>
              {colors.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: `repeat(${sizeKeys.length}, 1fr)`, gap: 8, marginBottom: 14 }}>
            {sizeKeys.map((s) => {
              const current = currentColorData?.sizes[s] ?? 0;
              const changed = Number(counts[s]) !== Number(current);
              return (
                <div key={s} className="field">
                  <label>Size {s}</label>
                  <input
                    className="input num"
                    data-testid={`adj-size-${s}`}
                    type="number"
                    min="0"
                    value={counts[s]}
                    onChange={(e) => updateCount(s, e.target.value)}
                    style={changed ? { borderColor: "#F59E0B" } : {}}
                  />
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>current: {current}</div>
                </div>
              );
            })}
          </div>

          <div className="field">
            <label>Reason *</label>
            <input className="input" data-testid="adj-reason" placeholder="e.g. 2 pieces damaged during handling" value={reason} onChange={(e) => setReason(e.target.value)}/>
          </div>

          {changedSizes.length > 0 && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 4, fontSize: 13, color: "#92400E" }}>
              Changing: {changedSizes.map((s) => `${s} (${currentColorData?.sizes[s] ?? 0} → ${counts[s]})`).join(", ")}
            </div>
          )}
        </div>
        <div className="modal-f">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" data-testid="save-adjustment-btn" disabled={busy || changedSizes.length === 0} onClick={save}>
            {busy ? "Saving..." : "Save Adjustment"}
          </button>
        </div>
      </div>
    </div>
  );
}
